import { AddToCartButtonType, AddToCartOrGroupCart } from '@commerce-cart-webcomponents/ui-composite';
import { DerivedAvailabilityStatusInternal } from '@commerce-product-webcomponents/sdk';
import {
  Button,
  ButtonIconPosition,
  ButtonSize,
  ButtonStyles,
  ButtonWidth,
  useFeatureVisibility,
  useTranslation,
} from '@nextgen-web-framework/all';
import { FC } from 'react';
import { FeatureFlags } from '../../../common/types';
import { ProductLink } from '../../Hoc';
import styles from './ConditionalButtonComponent.module.scss';
import type { ConditionalButtonPropsTypes, SubscriptionSelectButtonProps } from './types/propsTypes';

const ConditionalButtonComponent: FC<ConditionalButtonPropsTypes> = (
  props: ConditionalButtonPropsTypes,
): JSX.Element => {
  const t = useTranslation();
  const {
    disabled,
    entryObj,
    bundleSlots,
    className,
    size,
    addToCartButtonType = AddToCartButtonType.ADD_TO_CART_BUTTON,
    productData,
    showSubscriptionSelectButtonComp = false,
    onSubscriptionSelectButtonCB,
    derivedAvailabilityStatus,
    isMinProjection = true,
    onClick,
    showLink,
    isSop = false,
  } = props;

  const sopSellableOnly = useFeatureVisibility(FeatureFlags.sopSellableOnly);

  const derivedDisabledStatus =
    disabled ||
    ([DerivedAvailabilityStatusInternal.Nya, DerivedAvailabilityStatusInternal.Naw] as string[]).includes(
      derivedAvailabilityStatus || '',
    );
  const { productName, productFlags, productId, isBundle } = productData ?? {};
  const { restrictFromPdpDisplay, restrictFromSubscription, restrictFromAddToCart, isSellableForSOPOnly } =
    productFlags ?? {};
  const derivedBundleFlag = isBundle ?? false;
  const derivedProductId = productId ?? '';
  const derivedProductName = productName ?? '';
  const derivedShowLink = restrictFromPdpDisplay ? false : showLink;
  const isSubscribeBtnDisable = restrictFromPdpDisplay || restrictFromSubscription;
  const derivedShowLinkForSubscribeBtn = isSubscribeBtnDisable ? false : showLink;
  const derivedIsSopOnlySelling = (sopSellableOnly && isSellableForSOPOnly) ?? false;

  const addToCartButton = (
    <AddToCartOrGroupCart
      disabled={derivedDisabledStatus || restrictFromAddToCart}
      entryObj={{ ...entryObj, bundleSlots } as ConditionalButtonPropsTypes['entryObj']}
      size={size}
      addToCartButtonType={addToCartButtonType}
      productData={productData}
      isMinProjection={isMinProjection}
    />
  );

  const subscribeButton = (
    <ProductLink
      showLink={derivedShowLinkForSubscribeBtn}
      productId={derivedProductId}
      href=""
      productName={derivedProductName}
      isSop={true}
    >
      <Button
        onClick={onClick}
        id={derivedProductId}
        title={t('product.conditionalButtonComponent.subscribe')}
        buttonStyle={ButtonStyles.SOLID}
        size={size || ButtonSize.SMALL}
        data-testid="search-card-subscribe-btn-data-testid"
        buttonWidth={ButtonWidth.FULL_WIDTH}
        icon={{ iconName: 'refresh' }}
        iconPosition={ButtonIconPosition.LEFT}
        disabled={isSubscribeBtnDisable}
      />
    </ProductLink>
  );

  const viewBundleButton = (
    <ProductLink showLink={derivedShowLink} productId={derivedProductId} href="" productName={derivedProductName}>
      <Button
        onClick={onClick}
        id={derivedProductId}
        title={t('product.conditionalButtonComponent.viewBundle')}
        buttonStyle={ButtonStyles.SOLID}
        size={size || ButtonSize.SMALL}
        data-testid="view-bundle-btn"
        buttonWidth={ButtonWidth.FULL_WIDTH}
      />
    </ProductLink>
  );

  const viewSubscriptionSelectButton = (
    <Button
      id={derivedProductId}
      title={t('product.conditionalButtonComponent.subscriptionSelect.buttonTitle')}
      buttonStyle={ButtonStyles.SOLID}
      size={size || ButtonSize.SMALL}
      data-testid="view-subscription-select-btn"
      disabled={derivedDisabledStatus}
      onClick={() => {
        onSubscriptionSelectButtonCB?.(derivedProductId);
      }}
    />
  );

  const showSubscribeOrATC = derivedIsSopOnlySelling ? subscribeButton : addToCartButton;

  return (
    <div className={`${styles['cpw-conditional-button-container']} ${className}`}>
      {isSop && subscribeButton}
      {!isSop && (
        <>
          {showSubscriptionSelectButtonComp && viewSubscriptionSelectButton}
          {!showSubscriptionSelectButtonComp && <>{derivedBundleFlag ? viewBundleButton : showSubscribeOrATC}</>}
        </>
      )}
    </div>
  );
};

export default ConditionalButtonComponent;
export type { ConditionalButtonPropsTypes, SubscriptionSelectButtonProps };
